<template>
  <Modal
    :title="editing ? 'Edit meeting' : 'New meeting'"
    :subtitle="editing ? 'Changes reach everyone on the meeting.' : 'Set it up once; share the link with anyone in your school.'"
    size="md"
    @close="$emit('close')"
  >
    <form id="meeting-form" class="form" @submit.prevent="save">
      <div class="field">
        <label class="label" for="title">Title</label>
        <input
          id="title"
          class="input"
          v-model="title"
          placeholder="e.g. JSS2 Mathematics — Fractions"
          maxlength="120"
          autofocus
        />
      </div>

      <div class="field">
        <label class="label" for="description">Description <span class="optional">optional</span></label>
        <textarea
          id="description"
          class="input"
          v-model="description"
          rows="3"
          placeholder="What is this meeting about?"
        ></textarea>
      </div>

      <div class="field">
        <span class="label">Type</span>
        <div class="choices">
          <button type="button" class="choice" :class="{ active: mode === 'meeting' }" @click="mode = 'meeting'">
            <i class="ri-team-line"></i>
            <div>
              <strong>Meeting</strong>
              <span>Everyone can talk and share their camera.</span>
            </div>
          </button>
          <button type="button" class="choice" :class="{ active: mode === 'webinar' }" @click="mode = 'webinar'">
            <i class="ri-presentation-line"></i>
            <div>
              <strong>Class / webinar</strong>
              <span>Hosts present; others watch and raise a hand. Best for large groups.</span>
            </div>
          </button>
        </div>
      </div>

      <div class="field">
        <span class="label">Who can join</span>
        <div class="choices">
          <button type="button" class="choice" :class="{ active: !isPublic }" @click="isPublic = false">
            <i class="ri-lock-line"></i>
            <div>
              <strong>Invited only</strong>
              <span>Anyone else waits for a host to let them in.</span>
            </div>
          </button>
          <button type="button" class="choice" :class="{ active: isPublic }" @click="isPublic = true">
            <i class="ri-global-line"></i>
            <div>
              <strong>Anyone in the school</strong>
              <span>Everyone with the link joins straight in.</span>
            </div>
          </button>
        </div>
      </div>

      <div class="field">
        <span class="label">When</span>
        <div class="segmented">
          <button type="button" :class="{ active: !scheduled }" @click="scheduled = false" :disabled="editing && !!meeting.scheduled_at">
            <i class="ri-flashlight-line"></i> Whenever
          </button>
          <button type="button" :class="{ active: scheduled }" @click="scheduled = true">
            <i class="ri-calendar-event-line"></i> Schedule
          </button>
        </div>
        <Transition name="fade">
          <div class="when" v-if="scheduled">
            <div class="field">
              <label class="label" for="start">Starts</label>
              <input id="start" class="input" type="datetime-local" v-model="start" :min="minStart" />
            </div>
            <div class="field">
              <label class="label" for="duration">Length</label>
              <select id="duration" class="input" v-model.number="duration">
                <option v-for="d in durations" :key="d" :value="d">{{ label(d) }}</option>
              </select>
            </div>
          </div>
        </Transition>
        <span class="hint" v-if="scheduled">
          It goes into everyone's calendar, and they're reminded 15 minutes before.
        </span>
      </div>

      <div class="field" v-if="!editing">
        <span class="label">Invite people <span class="optional">optional</span></span>
        <PeoplePicker v-model="invitees" />
      </div>

      <div class="alert" v-if="error"><i class="ri-error-warning-line"></i>{{ error }}</div>
    </form>

    <template #footer>
      <button class="btn btn-secondary" type="button" @click="$emit('close')">Cancel</button>
      <button class="btn btn-primary" type="submit" form="meeting-form" :disabled="!title.trim() || saving">
        <span v-if="saving" class="spinner"></span>
        <template v-else>{{ editing ? "Save changes" : scheduled ? "Schedule meeting" : "Create meeting" }}</template>
      </button>
    </template>
  </Modal>
</template>

<script>
import Modal from "@/components/ui/Modal.vue";
import PeoplePicker from "@/components/ui/PeoplePicker.vue";
import { live, errorMessage } from "@/api";

const DURATIONS = [15, 30, 45, 60, 90, 120, 180];

// A local date-time as <input type="datetime-local"> wants it.
function localInput(date) {
  const d = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}

// The next quarter hour from now.
function nextQuarter() {
  const d = new Date();
  d.setMinutes(Math.ceil((d.getMinutes() + 5) / 15) * 15, 0, 0);
  return d;
}

// Create a meeting, or (with `meeting`) edit one. Emits saved with the
// meeting.
export default {
  name: "MeetingForm",
  components: { Modal, PeoplePicker },
  emits: ["close", "saved"],
  props: {
    meeting: { type: Object, default: null },
    // schedule opens a new meeting's form ready to pick a time.
    schedule: { type: Boolean, default: false },
  },
  data() {
    const m = this.meeting || {};
    return {
      title: m.title || "",
      description: m.description || "",
      mode: m.mode || "meeting",
      isPublic: !!m.public,
      scheduled: !!m.scheduled_at || this.schedule,
      start: localInput(m.scheduled_at ? new Date(m.scheduled_at) : nextQuarter()),
      duration: m.duration || 60,
      invitees: [],
      saving: false,
      error: "",
      durations: DURATIONS,
    };
  },
  computed: {
    editing() {
      return !!this.meeting;
    },
    minStart() {
      return localInput(new Date());
    },
  },
  methods: {
    label(minutes) {
      if (minutes < 60) return `${minutes} minutes`;
      const h = minutes / 60;
      return h === 1 ? "1 hour" : `${h} hours`;
    },
    body() {
      const body = {
        title: this.title.trim(),
        description: this.description.trim(),
        public: this.isPublic,
        mode: this.mode,
      };
      if (this.scheduled) {
        body.scheduled_at = new Date(this.start).toISOString();
        body.duration = this.duration;
      }
      return body;
    },
    async save() {
      if (this.scheduled && new Date(this.start) < new Date()) {
        this.error = "Pick a start time in the future";
        return;
      }
      this.saving = true;
      this.error = "";
      try {
        let response;
        if (this.editing) {
          response = await live.edit(this.meeting.live_id, this.body());
        } else {
          response = await live.create({ ...this.body(), users: this.invitees.map((u) => u.id) });
        }
        this.$toast.success(this.editing ? "Meeting updated" : "Meeting created", { position: "top", duration: 2500 });
        this.$emit("saved", response.data.data.live);
      } catch (e) {
        this.error = errorMessage(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.optional {
  margin-left: 0.3rem;
  font-weight: 500;
  color: var(--ink-3);
  font-size: 0.78rem;
}

.choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
}

.when {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 0.6rem;
  margin-top: 0.5rem;
}

.alert {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 0.85rem;
  border-radius: var(--radius);
  background: var(--danger-50);
  color: var(--danger-600);
  font-size: 0.88rem;
}
</style>
